import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Sidebar } from '@/components/shell/Sidebar';
import { Topbar, type Crumb } from '@/components/shell/Topbar';
import { useAlerts, useWorkflows } from '@/hooks/useSturmData';
import { AllRunsPage } from '@/pages/AllRuns';
import { NatsOverviewPage } from '@/pages/NatsOverview';
import { NotificationsPage } from '@/pages/Notifications';
import { RedisOverviewPage } from '@/pages/RedisOverview';
import { RunDetailPage } from '@/pages/RunDetail';
import { SettingsPage } from '@/pages/Settings';
import { WorkflowDetailPage } from '@/pages/WorkflowDetail';
import { WorkflowsPage } from '@/pages/Workflows';

function useCrumbs(): Crumb[] {
  const location = useLocation();
  const params = useParams();
  const segments = location.pathname.split('/').filter(Boolean);

  if (segments[0] === 'runs') {
    return [{ label: 'All runs' }];
  }

  if (segments[0] === 'notifications') {
    return [{ label: 'Notifications' }];
  }

  if (segments[0] === 'settings') {
    return [{ label: 'Settings' }];
  }

  if (segments[0] === 'inspect') {
    return [
      { label: 'Infrastructure' },
      { label: segments[1] === 'redis' ? 'Redis overview' : 'NATS overview' },
    ];
  }

  if (segments[0] === 'workflows') {
    const crumbs: Crumb[] = [{ label: 'Workflows', to: segments.length > 1 ? '/workflows' : undefined }];

    if (segments[1]) {
      crumbs.push({
        label: segments[1],
        to: segments.length > 2 ? `/workflows/${segments[1]}` : undefined,
      });
    }

    if (segments[2] === 'runs' && segments[3]) {
      crumbs.push({ label: params.runId ?? segments[3] });
    }

    return crumbs;
  }

  return [{ label: 'Sturm' }];
}

function Shell() {
  const { data: workflows = [] } = useWorkflows();
  const { data: alerts = [] } = useAlerts();
  const crumbs = useCrumbs();

  return (
    <div className="app">
      <Sidebar workflows={workflows} alerts={alerts} />
      <div className="main">
        <Topbar crumbs={crumbs} alerts={alerts} />
        <Routes>
          <Route path="/" element={<Navigate to="/workflows" replace />} />
          <Route path="/workflows" element={<WorkflowsPage />} />
          <Route path="/workflows/:slug" element={<WorkflowDetailPage />} />
          <Route path="/workflows/:slug/runs/:runId" element={<RunDetailPage />} />
          <Route path="/runs" element={<AllRunsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/inspect/nats" element={<NatsOverviewPage />} />
          <Route path="/inspect/redis" element={<RedisOverviewPage />} />
          <Route path="/auth/callback" element={<Navigate to="/workflows" replace />} />
          <Route path="*" element={<div className="page">Not found.</div>} />
        </Routes>
      </div>
    </div>
  );
}

export function App() {
  return <Shell />;
}
